import * as style from "./ApplicationList.module.scss";
import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { Box, Grid, TextField } from "@mui/material";
import { AppDispatch, RootState } from "../../store/store";
import Typography from "../../Components/Common/CprimeText";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
  setApplicationEditMode,
  fetchApplicationById,
} from "../../store/slices/applicationDetailsSlice";
import {
  editApplication,
  resubmitDeniedApplication,
  resetEditApplication,
} from "../../store/slices/CreateApplicationSlice";
import {
  Dropdown,
  DropdownOption,
  DropdownType,
  TextInput,
  Tooltip,
  Checkbox,
} from "cprime-foundational-components";
import Button from "../../Components/Common/CprimeButton";
import {
  getApplicationListApproverAdminData,
  getProtectedByData,
  getAppAdminGroupApproverGroupDropdownData,
  getValueStreamDropdownData,
  getEmailSearchResults,
  getAzureApplicationSearchResults,
} from "../../services/RequestAccess/RequestAccess";
import CommonLoader from "../../Components/CommonLoader/CommonLoader";
import { triggerToast } from "../../utils/toast";
import { getParamValue } from "../../utils/commonUtil";
import { useIdToken } from "../../hooks/useAuthSession";
import { searchContactsList } from "../../store/slices/userGroupSlice";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { StatusType } from "../../enums/RolesEnum";

interface ApplicationDetailsProps {
  setTabValue: React.Dispatch<React.SetStateAction<number>>;
}

interface DropdownData {
  selectedAdminIds: Array<DropdownOption>;
  adminData: [];
  selectedApproverIds: Array<DropdownOption>;
  approverData: [];
  selectedPublishApproverIds: Array<DropdownOption>;
  selectedProtectedBy: any;
  protectedByData: [];
  adminGroups?: DropdownOption[];
  approverGroups?: DropdownOption[];
  publishApprover?: DropdownOption[];
  valueStreamData: [];
  selectedValueStreamData: any;
  applicationApproverPayload?: any;
}

interface FormValues {
  app_cmdb_id: string;
  app_cmdb_name: string;
  primary_contact: string;
  business_owner: string;
  ad_app_guid: string;
  description: string;
  protected_by: string;
  value_stream_name: string;
  appadmin: string;
  approver: string;
  approverName?: string;
  publish_approver: string;
  primary_contact_name: string;
  business_owner_name: string;
  applicationApproverPayload: any;
  azure_ad_name: string;
  platformApp: boolean;
}

const ApplicationDetails: React.FC<ApplicationDetailsProps> = ({
  setTabValue,
}) => {
  const scssClass = style as any;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const idToken = useIdToken();
  const { data, status, editMode } = useSelector(
    (state: RootState) => state.applicationDetails,
  );

  const { editStatus, env } = useSelector(
    (state: RootState) => state.createApplication,
  );
  const { id } = useParams<{ id: string }>();

  // Refs for debounce cleanup
  const debouncedPrimaryContactRef = useRef<any>(null);
  const debouncedApproversRef = useRef<any>(null);
  const debouncedBusinessOwnerRef = useRef<any>(null);

  // Refs for tracking approvers request to prevent race conditions
  const approvalsRequestIdRef = useRef<number>(0);
  const approvalsAbortControllerRef = useRef<AbortController | null>(null);
  // const approversResponseRef = useRef<any>(null);
  const approverClearedRef = useRef<boolean>(false);
  const approverSelectedRef = useRef<boolean>(false);

  const [isApplicationAdminsDropdownOpen, setisApplicationAdminsDropdownOpen] =
    useState<boolean>(false);
  const [
    isApplicationPublishApproverDropdownOpen,
    setisApplicationPublishApproverDropdownOpen,
  ] = useState<boolean>(false);
  const [primaryContactOptions, setPrimaryContactOptions] = useState<
    DropdownOption[]
  >([]);
  const [approversOptions, setApproversOptions] = useState<DropdownOption[]>(
    [],
  );
  const [isApproversOptionsLoading, setIsApproversOptionsLoading] =
    useState<boolean>(false);
  const [searchText, setSearchText] = useState("");
  const [approveText, setApproveText] = useState("");
  const [businessOwnerOptions, setBusinessOwnerOptions] = useState<
    DropdownOption[]
  >([]);
  const [searchBO, setSearchBO] = useState("");
  const [azureAdOptions, setAzureAdOptions] = useState<DropdownOption[]>([]);
  const [azureAdSearchText, setAzureAdSearchText] = useState("");
  const [isAzureAdLoading, setIsAzureAdLoading] = useState<boolean>(false);
  const [isAzureAdCustom, setIsAzureAdCustom] = useState<boolean>(false);
  const azureAdSelectedRef = useRef<boolean>(false);

  const [dropdownState, setdropdownState] = useState<DropdownData>({
    selectedAdminIds: [],
    adminData: [],
    selectedApproverIds: [],
    selectedPublishApproverIds: [],
    approverData: [],
    selectedProtectedBy: [],
    protectedByData: [],
    adminGroups: [],
    approverGroups: [],
    selectedValueStreamData: [],
    valueStreamData: [],
  });

  // Handle state for input fields
  const [formValues, setFormValues] = useState<FormValues>({
    app_cmdb_id: "",
    app_cmdb_name: "",
    primary_contact: "",
    business_owner: "",
    ad_app_guid: "",
    description: "",
    protected_by: "",
    value_stream_name: "",
    appadmin: "",
    approver: "",
    publish_approver: "",
    primary_contact_name: "",
    business_owner_name: "",
    applicationApproverPayload: {},
    azure_ad_name: "",
    platformApp: false,
  });

  // Batch API calls to reduce multiple updates
  const fetchApplicationDataBatch = useCallback(
    async (appId: string) => {
      try {
        const [adminResp, protectedByResp, valueStreamResp, adminGrpResp] =
          await Promise.all([
            getApplicationListApproverAdminData(appId),
            getProtectedByData(),
            getValueStreamDropdownData(),
            getAppAdminGroupApproverGroupDropdownData(appId),
          ]);

        const adminGroups = adminGrpResp?.data?.group_data?.admin_group ?? [];
        const approverGroups =
          adminGrpResp?.data?.group_data?.approver_group ?? [];
        const publishApprover =
          adminGrpResp?.data?.group_data?.publish_group ?? [];

        const matchedAdminGroup = adminGroups?.find(
          (group: any) => group?.group_name === data?.appadmin,
        );
        const matchedApproverGroup = approverGroups?.find(
          (group: any) => group?.group_name === data?.approver,
        );
        const matchedAppAdminId = matchedAdminGroup?.id?.toString() || "";
        const matchedAppApproverId = matchedApproverGroup?.id?.toString() || "";
        const matchedAppPublishApproverId =
          publishApprover
            ?.find((group: any) => group?.group_name === data?.publish_approver)
            ?.id?.toString() || "";

        // Batch state updates into a single call
        setdropdownState({
          selectedAdminIds:
            adminResp?.data?.result?.app_admin !== null ||
            data.appadmin !== null
              ? adminResp?.data?.result?.app_admin
                  .filter((item: any) =>
                    data?.appadmin?.includes(item?.user_id),
                  )
                  .map((item: any) => {
                    return { id: item?.user_id, label: item?.user_name };
                  })
              : [],
          selectedApproverIds:
            adminResp?.data?.result?.approver !== null || data.approver !== null
              ? adminResp?.data?.result?.approver
                  .filter((item: any) =>
                    data?.approver?.includes(item?.user_id),
                  )
                  .map((item: any) => {
                    return { id: item?.user_id, label: item?.user_name };
                  })
              : [],
          selectedPublishApproverIds:
            adminResp?.data?.result?.publish_approver !== null ||
            data.publish_approver !== null
              ? adminResp?.data?.result?.publish_approver
                  .filter((item: any) =>
                    data?.publish_approver?.includes(item?.user_id),
                  )
                  .map((item: any) => {
                    return { id: item?.user_id, label: item?.user_name };
                  })
              : [],
          adminData: adminResp?.data?.result?.app_admin ?? [],
          approverData: adminResp?.data?.result?.approver ?? [],
          protectedByData: protectedByResp?.data ?? [],
          selectedProtectedBy: data?.protected_by_name || "",
          adminGroups:
            adminGrpResp?.data?.group_data?.admin_group?.map((item: any) => ({
              id: item.id,
              label: item.group_name,
            })) ?? [],
          approverGroups:
            adminGrpResp?.data?.group_data?.approver_group?.map(
              (item: any) => ({ id: item.id, label: item.group_name }),
            ) ?? [],
          publishApprover:
            adminGrpResp?.data?.group_data?.publish_group?.map((item: any) => ({
              id: item.id,
              label: item.group_name,
            })) ?? [],
          valueStreamData: valueStreamResp?.data ?? [],
          selectedValueStreamData:
            valueStreamResp?.data
              ?.filter(
                (item: any) =>
                  item?.value_stream_name === data?.value_stream_name,
              )
              ?.map((item: any) => ({
                id: item?.value_stream_id,
                label: item?.value_stream_name,
              })) ?? [],
        });

        setFormValues({
          app_cmdb_id: data?.app_cmdb_id || "",
          app_cmdb_name: data?.app_cmdb_name || "",
          primary_contact: data?.primary_contact || "",
          business_owner: data?.business_owner || "",
          ad_app_guid: data?.ad_app_guid || "",
          description: data?.description || "",
          protected_by: data?.protected_by_name || "",
          value_stream_name: data?.value_stream_name || "",
          appadmin: matchedAppAdminId || "",
          approver: matchedAppApproverId || "",
          primary_contact_name: data?.primary_contact_name || "",
          business_owner_name: data?.business_owner_name || "",
          publish_approver: matchedAppPublishApproverId || "",
          applicationApproverPayload: {},
          azure_ad_name: data?.azure_ad_name || "",
          platformApp: !!data?.platform_app,
        });
        setSearchBO(data?.business_owner || "");
        setSearchText(data?.primary_contact || "");
        setApproveText(data?.approver || "");
      } catch (error) {
        // console.error("Error fetching application data:", error);
      }
    },
    [data],
  );

  // Set tab value once on mount
  useEffect(() => {
    setTabValue(0);
  }, []); // Fixed: empty dependency array for one-time effect

  // Fetch application data when data changes
  useEffect(() => {
    if (data && id) {
      const applicationId = id?.split("-")[1];
      if (applicationId) {
        fetchApplicationDataBatch(applicationId);
      }
    }
  }, [id, data, fetchApplicationDataBatch]); // Fixed: added proper dependencies

  // Handle input changes
  const handleChange = useCallback((field: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Memoized validation checks
  // Appadmin/approver/publish_approver are hidden for Denied applications (see
  // the approverDetailsSection condition below), so they must be excluded here too -
  // otherwise Save stays disabled forever if those were empty when the app was denied.
  const fieldsExcludedFromValidation = useMemo(
    () =>
      data?.status === StatusType.DENIED
        ? ["appadmin", "approver", "publish_approver"]
        : [],
    [data?.status],
  );

  const isSubmitDisabled = useMemo(() => {
    return Object.entries(formValues).some(
      ([key, value]) =>
        !fieldsExcludedFromValidation.includes(key) &&
        value?.toString()?.trim() === "",
    );
  }, [formValues, fieldsExcludedFromValidation]);

  const isFormValid = useMemo(() => {
    return Object.entries(formValues).every(
      ([key, value]) =>
        fieldsExcludedFromValidation.includes(key) ||
        value?.toString()?.trim() !== "",
    );
  }, [formValues, fieldsExcludedFromValidation]);

  // Fetch primary contact options
  const fetchPrimaryContactOptions = useCallback(async (query: string) => {
    try {
      const { value, param } = getParamValue(query);
      const response = await getEmailSearchResults(
        encodeURIComponent(value),
        param,
      );
      const mapped: any[] = (response?.data || []).map((email: any) => ({
        id: email?.mail,
        label:
          param === "name"
            ? email?.mail?.toLowerCase()?.replace(/\./g, " ")
            : email?.mail,
        displayName: email?.displayName,
      }));
      setPrimaryContactOptions(mapped);
    } catch (error) {
      // console.error("Error fetching primary contact options:", error);
      setPrimaryContactOptions([]);
    }
  }, []);

  // Create debounced function with proper cleanup
  const debouncedFetchPrimaryContactOptions = useMemo(() => {
    const fn = debounce((val: string) => {
      fetchPrimaryContactOptions(val);
    }, 300);
    debouncedPrimaryContactRef.current = fn;
    return fn;
  }, [fetchPrimaryContactOptions]);

  // Fetch approvers options with race condition handling
  const fetchApproversOptions = useCallback(async (query: string) => {
    try {
      // Increment request ID to invalidate previous requests
      const currentRequestId = ++approvalsRequestIdRef.current;

      // Abort previous request if still pending
      if (approvalsAbortControllerRef.current) {
        approvalsAbortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      approvalsAbortControllerRef.current = abortController;

      setIsApproversOptionsLoading(true);
      const response = await searchContactsList(
        `api/groups/group-types-users?group_type=SailPoint&group_name=${query}`,
      );

      // Ignore response if a newer request has been made
      if (currentRequestId !== approvalsRequestIdRef.current) {
        return;
      }

      const groupMembersDetails = response?.data?.group_members_details ?? [];
      const dlList = response?.data?.dl_list ?? [];

      const mapped: any[] = (response?.data?.group_name || []).map(
        (groupName: { id: any; detail: any }) => ({
          id: groupName?.id,
          label: groupName?.detail,
          displayName: groupName?.detail,
        }),
      );

      setApproversOptions(mapped);
      setFormValues((prevValues: any) => ({
        ...prevValues,
        applicationApproverPayload: {
          group_name: response?.data?.group_name?.[0]?.detail ?? "",
          primary_contact:
            groupMembersDetails?.[0]?.detail?.split("|")[1]?.trim() ?? "",
          dl: dlList,
          group_members: groupMembersDetails.map((member: any) => {
            const name = member?.detail?.split("|")[0]?.trim() || "";
            const email = member?.detail?.split("|")[1]?.trim() || "";
            return `${name} | ${email}`;
          }),
        },
      }));
    } catch (error) {
      // Ignore abort errors (user initiated cancellation)
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      // console.error("Error fetching approvers options:", error);
      setApproversOptions([]);
    } finally {
      setIsApproversOptionsLoading(false);
    }
  }, []);

  // Create debounced function with proper cleanup
  const debouncedFetchApproversOptions = useMemo(() => {
    const fn = debounce((val: string) => {
      fetchApproversOptions(val);
    }, 300);
    debouncedApproversRef.current = fn;
    return fn;
  }, [fetchApproversOptions]);

  // Fetch business owner options
  const fetchBusinessOwnerOptions = useCallback(async (query: string) => {
    try {
      const { value, param } = getParamValue(query);
      const response = await getEmailSearchResults(
        encodeURIComponent(value),
        param,
      );
      const mapped: any[] = (response?.data || []).map((email: any) => ({
        id: email?.mail,
        label:
          param === "name"
            ? email?.mail?.toLowerCase()?.replace(/\./g, " ")
            : email?.mail,
        displayName: email?.displayName,
      }));
      setBusinessOwnerOptions(mapped);
    } catch (error) {
      // console.error("Error fetching business owner options:", error);
      setBusinessOwnerOptions([]);
    }
  }, []);

  // Create debounced function with proper cleanup
  const debouncedFetchBusinessOwnerOptions = useMemo(() => {
    const fn = debounce((val: string) => {
      fetchBusinessOwnerOptions(val);
    }, 300);
    debouncedBusinessOwnerRef.current = fn;
    return fn;
  }, [fetchBusinessOwnerOptions]);

  // Determine if current Protected By selection is AzureAD
  const selectedProtectedByLabel =
    Array.isArray(dropdownState?.selectedProtectedBy) &&
    dropdownState?.selectedProtectedBy?.length > 0
      ? dropdownState.selectedProtectedBy[0]?.label
      : ((
          dropdownState?.protectedByData as { id: number; name: string }[]
        )?.find((item) => item?.name === data?.protected_by_name)?.name ?? "");
  const isAzureAD = selectedProtectedByLabel === "AzureAD";

  const fetchAzureAdOptions = useCallback(async (query: string) => {
    setIsAzureAdLoading(true);
    try {
      const response = await getAzureApplicationSearchResults(query);
      const mapped: DropdownOption[] = (response?.data || []).map(
        (item: any) => ({
          id: item?.clientId,
          label: item?.displayName,
        }),
      );
      setAzureAdOptions(mapped);
    } catch {
      setAzureAdOptions([]);
    } finally {
      setIsAzureAdLoading(false);
    }
  }, []);

  const debouncedFetchAzureAdOptions = useMemo(
    () =>
      debounce((val: string) => {
        fetchAzureAdOptions(val);
      }, 300),
    [fetchAzureAdOptions],
  );

  // Commit a free-typed Azure AD name (not found in dropdown options)
  const commitAzureAdFreeText = useCallback(() => {
    if (azureAdSelectedRef.current) {
      azureAdSelectedRef.current = false;
      return;
    }
    const trimmed = azureAdSearchText?.trim();
    if (
      trimmed &&
      !azureAdOptions.some(
        (opt) => opt.label?.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      setFormValues((prev) => ({
        ...prev,
        azure_ad_name: trimmed,
        ad_app_guid: "", // cleared so user can enter manually
      }));
      setAzureAdSearchText(trimmed);
      setIsAzureAdCustom(true);
    }
  }, [azureAdSearchText, azureAdOptions]);

  // Inject typed text as a selectable "new" option when it doesn't match any existing option
  const azureAdOptionsWithNew = useMemo(() => {
    const trimmed = azureAdSearchText?.trim();
    if (
      trimmed &&
      !azureAdOptions.some(
        (opt) => opt.label?.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      return [
        ...azureAdOptions,
        { id: trimmed, label: trimmed, isNew: true } as any,
      ];
    }
    return azureAdOptions;
  }, [azureAdSearchText, azureAdOptions]);

  // Cleanup debounced functions and abort pending requests on unmount
  useEffect(() => {
    return () => {
      debouncedPrimaryContactRef.current?.cancel?.();
      debouncedApproversRef.current?.cancel?.();
      debouncedBusinessOwnerRef.current?.cancel?.();
      // Abort any pending approvers request
      if (approvalsAbortControllerRef.current) {
        approvalsAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle edit status and refresh data
  useEffect(() => {
    if (editStatus === "succeeded" && idToken && id) {
      triggerToast("Application updated successfully", "success");
      dispatch(resetEditApplication());
      dispatch(setApplicationEditMode(false));
      setApproversOptions([]);

      // Refetch application data to refresh the page
      const applicationId = id?.split("-")[1];
      if (applicationId) {
        dispatch(fetchApplicationById(Number(applicationId)) as any);
      }
    }
  }, [editStatus, id, dispatch, idToken]);

  // Handle cancel edit
  const cancelSaveEditApplication = useCallback(() => {
    dispatch(setApplicationEditMode(false));
    setApproveText(data?.approver || "");
    setApproversOptions([]);
    setdropdownState({
      ...dropdownState,
      selectedProtectedBy: [],
      selectedValueStreamData: [],
    });
  }, [dispatch, dropdownState, data?.approver]);

  // Handle save edit
  const handleSaveEditApplication = useCallback(async () => {
    const appId = id?.split("-")[1];

    const protectedBy =
      dropdownState?.selectedProtectedBy?.[0]?.id ??
      (
        dropdownState?.protectedByData as { id: number; name: string }[]
      )?.find((item) => item?.name === data?.protected_by_name)?.id ??
      null;
    const value_stream_id =
      dropdownState?.selectedValueStreamData?.[0]?.id ??
      (
        dropdownState?.valueStreamData as {
          value_stream_id: number;
          value_stream_name: string;
        }[]
      )?.find(
        (stream) => stream?.value_stream_name === data?.value_stream_name,
      )?.value_stream_id ??
      null;

    if (!isFormValid) return;

    if (data?.status === StatusType.DENIED) {
      const response = await dispatch(
        resubmitDeniedApplication({
          appId: appId ?? "",
          appCode: formValues?.app_cmdb_id,
          appGuid: formValues?.ad_app_guid,
          applicationName: formValues?.app_cmdb_name,
          description: formValues?.description,
          primaryContact: formValues?.primary_contact,
          primaryContactName: formValues?.primary_contact_name,
          businessOwner: formValues?.business_owner,
          businessOwnerName: formValues?.business_owner_name,
          protectedBy,
          value_stream_id,
          azure_ad_name: formValues?.azure_ad_name,
          platformApp: formValues?.platformApp,
          env,
        }),
      );
      if (response?.payload?.status === "SUCCESS") {
        triggerToast(
          response?.payload?.message || "Application resubmitted successfully",
          "success",
        );
        navigate("/application-list");
      } else {
        triggerToast(
          response?.payload?.error || "Failed to resubmit application",
          "error",
        );
      }
      return;
    }

    const editFormData = {
      id: appId ?? "",
      appCode: formValues?.app_cmdb_id,
      description: formValues?.description,
      primaryContact: formValues?.primary_contact,
      businessOwner: formValues?.business_owner,
      primaryContactName: formValues?.primary_contact_name,
      businessOwnerName: formValues?.business_owner_name,
      applicationName: formValues?.app_cmdb_name,
      appGuid: formValues?.ad_app_guid,
      appadmin: formValues?.appadmin,
      publishApprover: formValues?.publish_approver,
      approver: formValues?.approver,
      applicationApproverName: formValues?.approverName || formValues?.approver,
      azure_ad_name: formValues?.azure_ad_name,
      platformApp: formValues?.platformApp,
      ...(formValues?.applicationApproverPayload &&
        Object.keys(formValues.applicationApproverPayload).length > 0 && {
          applicationApproverPayload: formValues.applicationApproverPayload,
        }),
      protectedBy,
      value_stream_id,
    };
    const response = await dispatch(editApplication(editFormData));
    if (response?.payload?.status === "SUCCESS") {
      triggerToast(
        response?.payload?.message || "Application updated successfully",
        "success",
      );
    } else {
      triggerToast(
        response?.payload?.error || "Failed to update application",
        "error",
      );
    }
  }, [formValues, dropdownState, data, isFormValid, dispatch, id, env, navigate]);

  if (status === "loading" || editStatus === "loading")
    return <CommonLoader active />;

  // Use data from Redux instead of local state
  const applicationDetails = data;

  return (
    <div
      style={{
        // Denied/Revoked have no tab bar, so less height needs to be reserved above this
        // (confirmed 17.5rem via live DevTools testing vs 20.5rem for other statuses).
        maxHeight:
          data?.status === StatusType.DENIED ||
          data?.status === StatusType.REVOKED
            ? "calc(100vh - 17.5rem)"
            : "calc(100vh - 20.5rem)",
        overflowY: "auto",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      <Box className={scssClass.applicationDetails}>
        <Grid
          container
          spacing={2}
          className={scssClass.row}
          style={{ marginLeft: "0" }}
        >
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Typography className={scssClass.label}>Name</Typography>
            {editMode ? (
              <TextInput
                value={formValues?.app_cmdb_name}
                onChange={(e) => handleChange("app_cmdb_name", e.target.value)}
                fullWidth
                // sx={{
                //   width: "100%",
                //   "&.MuiFormControl-root": {
                //     width: "100%",
                //   },
                // }}
                // required
                placeholder="Enter Name"
                name="app_cmdb_name"
                error={formValues?.app_cmdb_name?.trim() === ""}
                errorMessage={
                  formValues?.app_cmdb_name?.trim() === ""
                    ? "Name is required"
                    : ""
                }
              />
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.app_cmdb_name || "-"}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Typography className={scssClass.label}>
              Primary Contact Email
            </Typography>
            {editMode ? (
              <Dropdown
                // style={{ marginTop: "0.625rem" }}
                width={"100%"}
                options={primaryContactOptions}
                aria-label="primaryContact"
                placeholder="Search And Select Contact"
                optionsTextLimit={200}
                inputValue={searchText}
                onOpen={() => fetchPrimaryContactOptions("")}
                onInputChange={(_, value) => {
                  setSearchText(value);
                  debouncedFetchPrimaryContactOptions(value?.trim());
                }}
                onChange={(selected: any[]) => {
                  if (selected?.length > 0 && selected[0]?.id) {
                    const selectedEmail = String(selected[0].id);
                    setSearchText(selectedEmail);
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      primary_contact: selectedEmail,
                      primary_contact_name: selected?.[0]?.displayName || "",
                    }));
                  } else {
                    setSearchText("");
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      primary_contact: "",
                      primary_contact_name: "",
                    }));
                  }
                }}
                selectedOptions={
                  searchText ? [{ id: searchText, label: searchText }] : []
                }
                multiple={false}
                type={DropdownType.FilterSingleSelect}
              />
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.primary_contact || "-"}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Typography className={scssClass.label}>
              Primary Contact Name
            </Typography>
            {editMode ? (
              <TextInput
                value={formValues?.primary_contact_name}
                fullWidth
                placeholder="Enter Primary Contact Name"
                error={formValues?.primary_contact_name?.trim() === ""}
                errorMessage={
                  formValues?.primary_contact_name?.trim() === ""
                    ? "Primary Contact Name is required"
                    : ""
                }
                disabled
              />
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.primary_contact_name || "-"}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          className={scssClass.row}
          style={{ marginLeft: "0" }}
        >
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Typography className={scssClass.label}>Value Stream</Typography>
            {editMode ? (
              <Dropdown
                // style={{ marginTop: "0.625rem" }}
                width={"100%"}
                aria-label="valueStream"
                placeholder="Select"
                optionsTextLimit={200}
                selectedOptions={
                  Array.isArray(dropdownState?.selectedValueStreamData) &&
                  dropdownState?.selectedValueStreamData?.length > 0
                    ? dropdownState?.selectedValueStreamData
                    : dropdownState?.valueStreamData
                        ?.filter(
                          (item: any) =>
                            item?.value_stream_name ===
                            applicationDetails?.value_stream_name,
                        )
                        ?.map((item: any) => ({
                          id: item?.value_stream_id,
                          label: item?.value_stream_name,
                        }))
                }
                multiple={false}
                options={
                  dropdownState?.valueStreamData?.length > 0
                    ? dropdownState?.valueStreamData?.map((item: any) => {
                        return {
                          id: item?.value_stream_id,
                          label: item?.value_stream_name,
                        };
                      })
                    : []
                }
                onChange={(field: any) => {
                  setdropdownState({
                    ...dropdownState,
                    selectedValueStreamData: field,
                  });
                }}
              />
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.value_stream_name || "-"}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Typography className={scssClass.label}>
              Business Owner Email
            </Typography>
            {editMode ? (
              <Dropdown
                // style={{ marginTop: "0.625rem" }}
                width={"100%"}
                options={businessOwnerOptions}
                aria-label="businessOwner"
                placeholder="Search And Select Contact"
                optionsTextLimit={200}
                inputValue={searchBO}
                onOpen={() => fetchBusinessOwnerOptions("")}
                onInputChange={(_, value) => {
                  setSearchBO(value);
                  debouncedFetchBusinessOwnerOptions(value?.trim());
                }}
                onChange={(selected: any[]) => {
                  if (selected?.length > 0 && selected[0]?.id) {
                    const selectedEmail = String(selected[0].id);
                    setSearchBO(selectedEmail);
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      business_owner: selectedEmail,
                      business_owner_name: selected?.[0]?.displayName || "",
                    }));
                  } else {
                    setSearchBO("");
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      business_owner: "",
                      business_owner_name: "",
                    }));
                  }
                }}
                selectedOptions={
                  searchBO ? [{ id: searchBO, label: searchBO }] : []
                }
                multiple={false}
                type={DropdownType.FilterSingleSelect}
              />
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.business_owner || "-"}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Typography className={scssClass.label}>
              Business Owner Name
            </Typography>
            {editMode ? (
              <TextInput
                value={formValues?.business_owner_name}
                fullWidth
                placeholder="Enter Business Owner Name"
                error={formValues?.business_owner_name?.trim() === ""}
                errorMessage={
                  formValues?.business_owner_name?.trim() === ""
                    ? "Business Owner Name is required"
                    : ""
                }
                disabled
              />
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.business_owner_name || "-"}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          className={scssClass.row}
          style={{ marginLeft: "0" }}
        >
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Typography className={scssClass.label}>Protected By</Typography>
            {editMode ? (
              <Dropdown
                // style={{ marginTop: "0.625rem" }}
                width={"100%"}
                placeholder="Select"
                optionsTextLimit={200}
                aria-label="protectedBy"
                selectedOptions={
                  Array.isArray(dropdownState?.selectedProtectedBy) &&
                  dropdownState?.selectedProtectedBy?.length > 0
                    ? dropdownState.selectedProtectedBy
                    : dropdownState?.protectedByData
                        ?.filter(
                          (item: any) =>
                            item?.name ===
                            applicationDetails?.protected_by_name,
                        )
                        .map((item: any) => {
                          return { id: item?.id, label: item?.name };
                        })
                }
                multiple={false}
                options={
                  dropdownState?.protectedByData?.length > 0
                    ? dropdownState?.protectedByData?.map((item: any) => {
                        return { id: item?.id, label: item?.name };
                      })
                    : []
                }
                onChange={(field: any) => {
                  setdropdownState({
                    ...dropdownState,
                    selectedProtectedBy: field,
                  });
                  // Reset azure AD fields when Protected By changes
                  setFormValues((prev) => ({
                    ...prev,
                    azure_ad_name: "",
                    ad_app_guid: "",
                  }));
                  setAzureAdSearchText("");
                  setAzureAdOptions([]);
                  setIsAzureAdCustom(false);
                  // setisProtectedByDropdownOpen(false);
                }}
              />
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.protected_by_name || "-"}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Typography className={scssClass.label}>
              Azure Application Name
            </Typography>
            {editMode ? (
              isAzureAD ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Dropdown
                    width={"100%"}
                    options={azureAdOptionsWithNew}
                    loading={isAzureAdLoading}
                    loadingText="Loading..."
                    aria-label="azureAdName"
                    placeholder="Search Or Enter Azure Application Name"
                    optionsTextLimit={200}
                    inputValue={azureAdSearchText}
                    selectedOptions={
                      formValues?.azure_ad_name
                        ? [
                            {
                              id:
                                formValues?.ad_app_guid ||
                                formValues?.azure_ad_name,
                              label: formValues?.azure_ad_name,
                            },
                          ]
                        : []
                    }
                    onOpen={() => fetchAzureAdOptions("")}
                    onInputChange={(_, value) => {
                      if (
                        formValues?.azure_ad_name &&
                        value !== formValues?.azure_ad_name
                      ) {
                        setFormValues((prev) => ({
                          ...prev,
                          azure_ad_name: "",
                          ad_app_guid: "",
                        }));
                        setIsAzureAdCustom(false);
                      }
                      setAzureAdSearchText(value);
                      setIsAzureAdLoading(true);
                      debouncedFetchAzureAdOptions(value?.trim());
                    }}
                    onClose={commitAzureAdFreeText}
                    onChange={(selected: any[]) => {
                      azureAdSelectedRef.current = true;
                      const isNewOption = !!selected?.[0]?.isNew;
                      const selectedClientId = isNewOption
                        ? ""
                        : String(selected?.[0]?.id || "");
                      const selectedDisplayName = String(
                        selected?.[0]?.label || "",
                      );
                      setFormValues((prev) => ({
                        ...prev,
                        azure_ad_name: selectedDisplayName,
                        ad_app_guid: selectedClientId,
                      }));
                      setAzureAdSearchText("");
                      setIsAzureAdCustom(isNewOption);
                    }}
                    renderOption={(props: any, option: any) => (
                      <li {...props}>
                        {option?.isNew ? (
                          <Tooltip
                            title="User-provided value (not from AzureAD)"
                            placement="right"
                            arrow
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                justifyContent: "space-between",
                              }}
                            >
                              <span>{option.label}</span>
                              <AddCircleOutlineIcon
                                style={{
                                  fontSize: "16px",
                                  color: "#000000",
                                  marginLeft: "8px",
                                  flexShrink: 0,
                                }}
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <span>{option.label}</span>
                        )}
                      </li>
                    )}
                    multiple={false}
                    type={DropdownType.FilterSingleSelect}
                  />
                </div>
              ) : (
                <TextInput
                  value={formValues?.azure_ad_name}
                  onChange={(e: any) =>
                    handleChange("azure_ad_name", e.target.value)
                  }
                  fullWidth
                  placeholder="Enter Azure Application Name"
                  error={formValues?.azure_ad_name?.trim() === ""}
                  errorMessage={
                    formValues?.azure_ad_name?.trim() === ""
                      ? "Azure Application Name is required"
                      : ""
                  }
                />
              )
            ) : (
              <Typography className={scssClass.value}>
                {(applicationDetails as any)?.azure_ad_name || "-"}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Typography className={scssClass.label}>
              Azure Application GUID
            </Typography>
            {editMode ? (
              <TextInput
                value={formValues?.ad_app_guid}
                onChange={(e: any) =>
                  handleChange("ad_app_guid", e.target.value)
                }
                fullWidth
                placeholder="Enter Enrolled Group"
                disabled={isAzureAD && !isAzureAdCustom}
                error={!isAzureAD && formValues?.ad_app_guid?.trim() === ""}
                errorMessage={
                  !isAzureAD && formValues?.ad_app_guid?.trim() === ""
                    ? "AzureAD AppId (ClientId) is required"
                    : ""
                }
              />
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.ad_app_guid || "-"}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          className={scssClass.row}
          style={{ marginLeft: "0", justifyContent: "flex-start" }}
        >
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Typography className={scssClass.label}>
                Application Code
              </Typography>
              <Tooltip
                title="Abbreviated app name used when referencing the application"
                placement="right"
                arrow
              >
                <InfoOutlinedIcon sx={{ fontSize: "16px", color: "#58595b" }} />
              </Tooltip>
            </div>
            {editMode ? (
              <TextInput
                value={formValues?.app_cmdb_id}
                onChange={(e: any) =>
                  handleChange(
                    "app_cmdb_id",
                    e.target.value?.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6),
                  )
                }
                fullWidth
                maxLength={6}
                // sx={{
                //   "&.MuiFormControl-root": {
                //     width: "100%",
                //   },
                // }}
                // required
                placeholder="Enter 3-6 Character Application Code"
                error={formValues?.app_cmdb_id?.trim() === ""}
                errorMessage={
                  formValues?.app_cmdb_id?.trim() === ""
                    ? "3-6 Character Application Code is required"
                    : ""
                }
              />
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.app_cmdb_id || "-"}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={4}
            className={scssClass.column}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              justifyContent: editMode ? "flex-end" : "flex-start",
            }}
          >
            {editMode ? (
              <Checkbox
                label="Platform Application"
                checked={!!formValues?.platformApp}
                onChange={() =>
                  setFormValues((prev) => ({
                    ...prev,
                    platformApp: !prev?.platformApp,
                  }))
                }
              />
            ) : (
              <>
                <Typography className={scssClass.label}>
                  Platform Application
                </Typography>
                <Typography className={scssClass.value}>
                  {applicationDetails?.platform_app ? "True" : "False"}
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          className={scssClass.row}
          style={{ marginLeft: "0" }}
        >
          <Grid item xs={12} className={scssClass.column}>
            <Typography className={scssClass.label}>Description</Typography>
            {editMode ? (
              <>
                <TextField
                  fullWidth
                  name="description"
                  value={formValues?.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  inputProps={{ maxLength: 300 }}
                  multiline
                  rows={2}
                  placeholder="Enter description"
                  error={formValues?.description?.trim() === ""}
                  helperText={
                    editMode && formValues?.description?.trim() === ""
                      ? "Description is required"
                      : ""
                  }
                  disabled={!editMode}
                  sx={{ marginTop: "8px" }}
                />
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography className={scssClass.label}>
                    {formValues?.description?.length}/300
                  </Typography>
                </div>
              </>
            ) : (
              <Typography className={scssClass.value}>
                {applicationDetails?.description || "-"}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Action Buttons */}
      </Box>

      {applicationDetails?.status !== StatusType.DENIED && (
        <>
          <div
            style={{
              backgroundColor: "#F8F8F9",
              padding: "0.5rem",
            }}
          ></div>

          <Box className={scssClass.approverDetailsSection}>
            <Grid
            container
            spacing={2}
            className={scssClass.row}
            style={{ marginLeft: "0" }}
          >
            <Grid
              item
              xs={4}
              className={scssClass.column}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <Typography className={scssClass.label}>
                Application Admins
              </Typography>
              {editMode ? (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <Dropdown
                    //disabled={enableApplicationAdminDropdown}
                    style={{ width: "100%" }}
                    // style={{ width: "26vw", marginTop: "0.625rem" }}
                    open={isApplicationAdminsDropdownOpen}
                    error={formValues?.appadmin?.trim() === ""}
                    placeholder="Select"
                    optionsTextLimit={200}
                    aria-label="appAdmin"
                    onOpen={() => setisApplicationAdminsDropdownOpen(true)}
                    onClose={() => setisApplicationAdminsDropdownOpen(false)}
                    selectedOptions={
                      dropdownState?.selectedAdminIds?.length > 0
                        ? dropdownState.selectedAdminIds
                        : applicationDetails?.appadmin
                          ? [
                              {
                                id: applicationDetails?.appadmin,
                                label: applicationDetails?.appadmin,
                              },
                            ]
                          : []
                    }
                    options={dropdownState?.adminGroups ?? []}
                    onChange={(field: DropdownOption[]) => {
                      setdropdownState({
                        ...dropdownState,
                        selectedAdminIds: field,
                      });
                      setFormValues({
                        ...formValues,
                        appadmin: String(field?.[0]?.id || ""),
                      });
                      setisApplicationAdminsDropdownOpen(false);
                    }}
                  />

                  {/* <span
                    onClick={() => setEnableApplicationAdminDropdown(false)}
                    style={{ cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                  >
                    <EditIcon />
                  </span> */}
                </div>
              ) : (
                <Typography className={scssClass.value}>
                  {applicationDetails?.appadmin || "-"}
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={4}
              className={scssClass.column}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <Typography className={scssClass.label}>Approvers</Typography>
              {editMode ? (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <Dropdown
                    style={{ width: "100%" }}
                    error={formValues?.approver?.trim() === ""}
                    placeholder="Select"
                    optionsTextLimit={200}
                    aria-label="approverselect"
                    loading={isApproversOptionsLoading}
                    loadingText="Loading..."
                    options={
                      approversOptions?.length > 0
                        ? approversOptions
                        : [
                            ...((dropdownState?.approverGroups ?? [])?.map(
                              (item: any) => ({
                                id: item.id,
                                label: item.label,
                              }),
                            ) ?? []),
                          ]
                    }
                    inputValue={approveText}
                    onInputChange={(_, value) => {
                      if (approverClearedRef.current) {
                        approverClearedRef.current = false;
                        setApproveText("");
                        setApproversOptions([]);
                        return;
                      }
                      if (approverSelectedRef.current) {
                        approverSelectedRef.current = false;
                        setApproveText(value);
                        return;
                      }
                      setApproveText(value);
                      if (value?.trim()) {
                        setIsApproversOptionsLoading(true);
                        debouncedFetchApproversOptions(value?.trim());
                      } else {
                        setIsApproversOptionsLoading(false);
                        setApproversOptions([]);
                      }
                    }}
                    onChange={(selected: any[]) => {
                      if (selected?.length > 0 && selected[0]?.label) {
                        const selectedApprover = String(selected[0].label);
                        const selectedId = selected[0]?.id;
                        approverSelectedRef.current = true;
                        setApproveText(selectedApprover);

                        // Check if selected group is an existing group from app-groups API
                        const isExistingGroup = (
                          dropdownState?.approverGroups ?? []
                        ).some((group: any) => group.id === selectedId);

                        if (isExistingGroup) {
                          // Existing group — just send the group ID, no payload needed
                          setFormValues((prevValues) => ({
                            ...prevValues,
                            approver: String(selectedId),
                            approverName: selectedApprover,
                            applicationApproverPayload: null,
                          }));
                        } else {
                          // New group from search — payload already built by fetchApproversOptions
                          setFormValues((prevValues) => ({
                            ...prevValues,
                            approver: selectedApprover,
                            approverName: selectedApprover,
                          }));
                        }
                      } else {
                        approverClearedRef.current = true;
                        setApproveText("");
                        setFormValues((prevValues) => ({
                          ...prevValues,
                          approver: "",
                          applicationApproverPayload: null,
                        }));
                      }
                    }}
                    selectedOptions={
                      approveText
                        ? [{ id: approveText, label: approveText }]
                        : []
                    }
                    multiple={false}
                    type={DropdownType.FilterSingleSelect}
                  />
                </div>
              ) : (
                <Typography className={scssClass.value}>
                  {applicationDetails?.approver || "-"}
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={4}
              className={scssClass.column}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <Typography className={scssClass.label}>
                Publish Approver
              </Typography>
              {editMode ? (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <Dropdown
                    //disabled={enableApplicationApproverDropdown}
                    style={{ width: "100%" }}
                    // style={{ width: "26vw", marginTop: "0.625rem" }}
                    error={formValues?.publish_approver?.trim() === ""}
                    open={isApplicationPublishApproverDropdownOpen}
                    placeholder="Select"
                    optionsTextLimit={200}
                    aria-label="approverselect"
                    onOpen={() =>
                      setisApplicationPublishApproverDropdownOpen(true)
                    }
                    onClose={() =>
                      setisApplicationPublishApproverDropdownOpen(false)
                    }
                    selectedOptions={
                      dropdownState?.selectedPublishApproverIds?.length > 0
                        ? dropdownState?.selectedPublishApproverIds
                        : applicationDetails?.publish_approver
                          ? [
                              {
                                id: applicationDetails?.publish_approver,
                                label: applicationDetails?.publish_approver,
                              },
                            ]
                          : []
                    }
                    options={dropdownState?.publishApprover ?? []}
                    onChange={(field: DropdownOption[]) => {
                      setdropdownState({
                        ...dropdownState,
                        selectedPublishApproverIds: field,
                      });
                      setFormValues({
                        ...formValues,
                        publish_approver: String(field?.[0]?.id || ""),
                      });
                      setisApplicationPublishApproverDropdownOpen(false);
                    }}
                  />
                  {/* <span
                    onClick={() => setEnableApplicationPublishApproverDropdown(false)}
                    style={{ cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                  >
                    <EditIcon />
                  </span> */}
                </div>
              ) : (
                <Typography className={scssClass.value}>
                  {applicationDetails?.publish_approver || "-"}
                </Typography>
              )}
            </Grid>
          </Grid>
          </Box>

          <div
            style={{
              backgroundColor: "#F8F8F9",
              padding: "0.5rem",
            }}
          ></div>
        </>
      )}

      {editMode && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            height: "3.5rem",
            marginTop: data?.status === StatusType.DENIED ? 0 : "1rem",
            marginBottom: data?.status === StatusType.DENIED ? 0 : "1rem",
            backgroundColor: "#fff",
          }}
        >
          <div style={{ padding: "0.5rem" }}>
            <Button
              variant="tertiary"
              sx={{ marginRight: "10px" }}
              size="medium"
              onClick={() => cancelSaveEditApplication()}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isSubmitDisabled}
              size="medium"
              onClick={() => handleSaveEditApplication()}
            >
              {data?.status === StatusType.DENIED ? "Resubmit" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetails;
